/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import parse from 'html-react-parser';
import {error} from '@ion/core/src/logger/logger';
import {GenericObject} from '@ion/core/src/types/entities';
import {convertBase64ToString} from '@ion/core/src/utils/Encoding';
import {
  Notification,
  NotificationDetailsType
} from '../types/extensions';

/**
 * Parses and pre-renders the given alert notification by type; either as html,
 * or plain text.
 */
export const prerender = ({details}: Notification) => {
  const results: GenericObject = {};
  if (details) {
    if (details.email) {
      results.content = parse(renderMarkup(details));
      results.type    = NotificationDetailsType.email;
    } else if (details.sms) {
      results.content = parse(renderPlainText(details));
      results.type    = NotificationDetailsType.sms;
    }
  }
  return results;
};

/**
 * Given an encoded Notification Action template, decodes the encoded document string
 * and returns the top-level div container for rendering to the DOM.
 */
export const renderMarkup = ({email}: GenericObject, includeTable = true) => {
  const parser = new DOMParser();
  try {
    const decoded  = convertBase64ToString(email.body);
    const element  = parser.parseFromString(decoded, 'text/html');
    const results  = element.querySelector('notificationbody') || element.querySelector('body');
    const rootNode = results.children[0];
    const table    = includeTable ? rootNode.querySelector('table') : undefined;
    if (rootNode.nodeName === 'DIV' && rootNode.className === 'container') {
      return rootNode.innerHTML;
    }
    return table ? table.innerHTML : results.innerHTML;
  } catch (err) {
    error(`An Error occurred parsing Notification Markup: ${err.toString()}`);
    return '';
  }
};

/**
 * Given an encoded Notification Action template, decodes the encoded sms plain-text string
 * and returns an html formatted representation.
 */
const renderPlainText = ({sms}: GenericObject) => {
  try {
    const decoded = convertBase64ToString(sms.body);
    const items   = decoded.split(/(?:\r\n|\r|\n)/g);
    let formatted = '<table><tbody>';
    items?.forEach(part => {
      const fields = part.split(':');
      const length = fields.length;
      if (length > 2) {
        let value = '';
        formatted += `<tr><td>${fields[0]}</td>:`;
        fields.forEach((val, i) => {
          if (i > 0) {
            value += `${val}:`;
          }
        });
        formatted += `<td>${value.substring(0, value.lastIndexOf(':'))}</td></tr>`;
      } else if (length === 2) {
        formatted += `<tr><td>${fields[0]}</td>:<td>${fields[1]}</td></tr>`;
      }
    });
    formatted += '</tbody></table>';
    return formatted.replace(/(:){2}/g, '');
  } catch (err) {
    return '';
  }
};
